import React from 'react';
import { useMutation } from '@apollo/client';
import EndMeetingComponent from './component';
import logger from '/imports/startup/client/logger';
import { MEETING_END } from './mutations';
import { USER_AGGREGATE_COUNT_SUBSCRIPTION } from '/imports/ui/core/graphql/queries/users';
import useDeduplicatedSubscription from '../../core/hooks/useDeduplicatedSubscription';
import useMeeting from '../../core/hooks/useMeeting';
import { exportWhiteboardData, logoutUser } from '../app/service';
import Auth from '/imports/ui/services/auth';
import { USER_LEAVE_MEETING } from '../../core/graphql/mutations/userMutations';
import Session from '/imports/ui/services/storage/in-memory';
import { captureWhiteboardState, getStateSummary } from '../whiteboard/state-capture';

const END_MEETING_DONE_TIMEOUT_MS = 10000; // 10 second timeout for end-meeting-done event

const EndMeetingContainer = (props) => {
  const [meetingEnd] = useMutation(MEETING_END);
  const [userLeaveMeeting] = useMutation(USER_LEAVE_MEETING);
  const {
    data: countData,
  } = useDeduplicatedSubscription(USER_AGGREGATE_COUNT_SUBSCRIPTION);
  const users = countData?.user_aggregate?.aggregate?.count || 0;

  const { setIsOpen, courseDetails } = props;
  const LOGOUT_CODE = '680';

  const endMeeting = async () => {
    logger.warn({
      logCode: 'moderator_forcing_end_meeting',
      extraInfo: { logType: 'user_action' },
    }, 'this user clicked on EndMeeting and confirmed, removing everybody from the meeting');

    // STEP 1: Tell another page: "I want to end meeting - save your data first!"
    window.dispatchEvent(
      new CustomEvent("end-meeting-req", {
        detail: { isEndReq: true }
      })
    );

    // STEP 2: Wait for the done signal (with timeout to prevent deadlock)
    await waitForEndMeetingDone();

    // Capture whiteboard state before ending meeting
    const editor = window.tlEditorRef?.current;
    // Get current slide from tldraw editor's current page
    const currentSlide = editor?.getCurrentPageId ?
      parseInt(editor.getCurrentPageId().replace('page:', ''), 10) || 1 : 1;

    const whiteboardState = captureWhiteboardState(
      editor,
      courseDetails?.course?.courseDetail?.presentationId,
      currentSlide
    );

    logger.info({
      logCode: 'whiteboard_export_end_meeting',
      extraInfo: { summary: getStateSummary(whiteboardState) },
    }, 'Exporting whiteboard state on end meeting');

    const meetingId = Auth?._meetingID;
    const hasImportedKey = `hasImported-${meetingId}`;

    // CRITICAL: Only export if we have valid whiteboard state with content
    // This prevents overwriting existing annotations with an empty state
    const hasValidState = whiteboardState && whiteboardState.slides && whiteboardState.slides.length > 0;

    // Await the export to ensure data is saved before ending meeting
    try {
      if (hasValidState) {
        const exportApiPayload = {
          internalMeetingID: Auth?._meetingID,
          externalUserID: Auth?._externUserID,
          sessionId: courseDetails?.course?.courseDetail?.courseSessionId,
          state: whiteboardState,
        };

        await exportWhiteboardData(exportApiPayload);
        logger.info({
          logCode: 'whiteboard_export_success',
        }, 'Whiteboard export completed successfully');
      } else {
        logger.warn({
          logCode: 'whiteboard_export_skipped_empty',
          extraInfo: { hasState: !!whiteboardState, hasSlides: !!whiteboardState?.slides },
        }, 'Skipping whiteboard export - no valid state to save (preserving existing annotations)');
      }
      localStorage.removeItem(hasImportedKey);
      localStorage.removeItem('pageZoomMap');
    } catch (error) {
      logger.error({
        logCode: 'whiteboard_export_failed',
        extraInfo: { error: error?.message },
      }, 'Whiteboard export failed');
    }

    const apiPayload = {
      internalMeetingID: Auth?._meetingID,
      externalUserID: Auth?._externUserID,
      courseSessionId: courseDetails?.course?.courseDetail?.courseSessionId,
      isPrepareLesson: courseDetails?.course?.courseDetail?.isPrepareLesson
    };

    try {
      await logoutUser(apiPayload);
      setIsOpen(false);
    } catch (error) {
      logger.error({
        logCode: 'logout_user_failed',
        extraInfo: { error: error?.message },
      }, 'Logout user failed');
    } finally {
      meetingEnd();
    }
  };

  const waitForEndMeetingDone = () => {
    return new Promise((resolve) => {
      let timeoutId;

      const handler = (event) => {
        if (event.detail?.isEndDone) {
          clearTimeout(timeoutId);
          window.removeEventListener("end-meeting-done", handler);
          resolve(true);
        }
      };

      window.addEventListener("end-meeting-done", handler);

      // Timeout to prevent deadlock if event is never dispatched
      timeoutId = setTimeout(() => {
        window.removeEventListener("end-meeting-done", handler);
        logger.warn({
          logCode: 'end_meeting_done_timeout',
        }, 'Timeout waiting for end-meeting-done event, proceeding anyway');
        resolve(false);
      }, END_MEETING_DONE_TIMEOUT_MS);
    });
  };

  const { data: meeting } = useMeeting((m) => ({
    name: m.name,
  }));

  return (
    <EndMeetingComponent
      endMeeting={endMeeting}
      users={users}
      meetingTitle={meeting?.name}
      {...props}
    />
  );
};

export default EndMeetingContainer;
